import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Search, Shield } from 'lucide-react'
import { useRoles } from '../hooks'
import type { Role } from '@/types/entities'

interface UserRolesSelectionDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    selectedRoleIds: number[]
    onSelectionChange: (roleIds: number[]) => void
    disabled?: boolean
}

export function UserRolesSelectionDialog({
    open,
    onOpenChange,
    selectedRoleIds,
    onSelectionChange,
    disabled = false,
}: UserRolesSelectionDialogProps) {
    const { t, i18n } = useTranslation()
    const { data: rolesResponse } = useRoles()
    const availableRoles = rolesResponse?.data || []
    const [searchQuery, setSearchQuery] = useState('')
    const [localSelection, setLocalSelection] = useState<number[]>(selectedRoleIds)

    // Update local selection when selectedRoleIds prop changes
    useEffect(() => {
        setLocalSelection(selectedRoleIds)
    }, [selectedRoleIds, open])

    // Filter roles based on search query
    const filteredRoles = availableRoles.filter((role) => {
        const query = searchQuery.toLowerCase()
        const roleName = i18n.exists(`roles.${role.name}`) ? t(`roles.${role.name}`) : role.name
        const roleDescription = i18n.exists(`roles.${role.name}Description`) 
            ? t(`roles.${role.name}Description`) 
            : role.description || ''
        return (
            roleName.toLowerCase().includes(query) ||
            roleDescription.toLowerCase().includes(query)
        )
    })

    const handleToggleRole = (roleId: number) => {
        if (disabled) return
        
        setLocalSelection((prev) => {
            if (prev.includes(roleId)) {
                return prev.filter((id) => id !== roleId)
            } else {
                return [...prev, roleId]
            }
        })
    }

    const handleSelectAll = () => {
        if (disabled) return
        const allRoleIds = filteredRoles.map((role) => role.id)
        setLocalSelection(allRoleIds)
    }

    const handleDeselectAll = () => {
        if (disabled) return
        setLocalSelection([])
    }

    const handleSave = () => {
        // Validate: must have at least one role selected
        if (localSelection.length === 0) {
            // This should be caught by form validation, but show error if somehow reached
            return
        }
        
        onSelectionChange(localSelection)
        onOpenChange(false)
    }

    const handleCancel = () => {
        setLocalSelection(selectedRoleIds) // Reset to original selection
        onOpenChange(false)
    }

    const selectedCount = localSelection.length
    const filteredSelectedCount = filteredRoles.filter((role) =>
        localSelection.includes(role.id)
    ).length

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>{t('users.selectRoles')}</DialogTitle>
                    <DialogDescription>
                        {t('users.selectRolesDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col flex-1 min-h-0 space-y-4">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder={t('users.searchRoles')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>

                    {/* Selection actions */}
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                            {t('users.selectedRolesCount', { count: selectedCount })}
                            {searchQuery && (
                                <span className="ml-2">
                                    ({t('users.filteredSelectedCount', { count: filteredSelectedCount, total: filteredRoles.length })})
                                </span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleSelectAll}
                                disabled={disabled || filteredRoles.length === 0}
                            >
                                {t('users.selectAll')}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleDeselectAll}
                                disabled={disabled || filteredSelectedCount === 0}
                            >
                                {t('users.deselectAll')}
                            </Button>
                        </div>
                    </div>

                    {/* Roles list */}
                    <div className="flex-1 overflow-y-auto border rounded-md min-h-[300px] max-h-[400px]">
                        {filteredRoles.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-center p-4">
                                <Shield className="h-12 w-12 text-muted-foreground mb-2" />
                                <p className="text-sm text-muted-foreground">
                                    {searchQuery
                                        ? t('users.noRolesFound')
                                        : t('users.noRolesAvailable')}
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="sticky top-0 bg-background border-b z-10">
                                        <tr>
                                            <th className="text-left p-3 text-sm font-medium w-12">
                                                <span className="sr-only">{t('users.select')}</span>
                                            </th>
                                            <th className="text-left p-3 text-sm font-medium">
                                                {t('users.role')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRoles.map((role) => {
                                            const isSelected = localSelection.includes(role.id)
                                            const checkboxId = `role-${role.id}`
                                            const roleName = i18n.exists(`roles.${role.name}`) ? t(`roles.${role.name}`) : role.name
                                            const roleDescription = i18n.exists(`roles.${role.name}Description`) 
                                                ? t(`roles.${role.name}Description`) 
                                                : role.description || ''
                                            return (
                                                <tr
                                                    key={role.id}
                                                    className="border-b hover:bg-accent transition-colors"
                                                >
                                                    <td className="p-3">
                                                        <Checkbox
                                                            id={checkboxId}
                                                            checked={isSelected}
                                                            onCheckedChange={() => handleToggleRole(role.id)}
                                                            disabled={disabled}
                                                        />
                                                    </td>
                                                    <td className="p-3">
                                                        <Label
                                                            htmlFor={checkboxId}
                                                            className="cursor-pointer"
                                                        >
                                                            <div className="font-medium">{roleName}</div>
                                                            {roleDescription && (
                                                                <div className="text-sm text-muted-foreground mt-1">
                                                                    {roleDescription}
                                                                </div>
                                                            )}
                                                        </Label>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer actions */}
                <div className="flex justify-end space-x-2 pt-4 border-t">
                    <Button variant="outline" onClick={handleCancel} disabled={disabled}>
                        {t('userForm.cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={disabled}>
                        {t('common.save')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
